import { useApi } from '@/api';
import { LinkCard } from '@/components';
import { useSparkColor } from '@/hooks';
import {
  ArrowUpRightBoxIcon,
  CurrencyValue,
  Flex,
  FlexProps,
  NetworkIcon,
  TableSkeleton
} from '@blockanalitica/ui';
import _ from 'lodash';
import styled, { css } from 'styled-components';

type LinkCardData = {
  total_supply: string;
  total_supply_change: string;
  total_borrow: string;
  previous_total_borrow: string;
  total_borrow_change: string;
  tvl: string;
  tvl_change: string;
  total_available: string;
  total_available_change: string;
  network: string;
  suppliers: number;
  borrowers: number;
};

const KpiTitle = styled(Flex)<FlexProps>`
  ${(props) => css`
    color: ${props.theme.colors.muted};
    font-size: ${props.theme.sizes['-1']};
  `}
`;

export default function NetworksLinkCard() {
  const color = useSparkColor();
  const [data, error] = useApi<LinkCardData[]>({
    url: '/sparkstar/sparklend/networks/',
    queryParams: {
      p: 1,
      p_size: 10,
      order: '-total_supply'
    }
  });

  if (error) {
    throw error;
  }
  if (!data) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  return (
    <Flex flexDirection={['column', 'row']} gap="s">
      {data.map((row) => (
        <LinkCard
          href={`https://spark.blockanalitica.com/v1/${row.network}`}
          target="_blank"
          rel="noreferrer"
          key={row.network}
          gap="s"
          justifyContent="space-between"
          flexDirection="column"
          hoverBorderColor={color}>
          <Flex flexDirection="column" gap="s">
            <Flex gap="2xs" alignItems="center" justifyContent="space-between">
              <Flex>
                <NetworkIcon network={row.network} size="2" />
                {_.capitalize(row.network)}
              </Flex>
              <Flex
                justifyContent="flex-end"
                fontSize="-1"
                color="link"
                gap="3xs"
                alignItems="center">
                Visit SparkLend
                <ArrowUpRightBoxIcon size="-1" />
              </Flex>
            </Flex>
            <Flex gap="xs" justifyContent="space-between">
              <Flex
                flexDirection="column"
                flex="1"
                gap="3xs"
                alignItems="flex-start">
                <KpiTitle>Total Market Size</KpiTitle>
                <CurrencyValue
                  value={row.total_supply}
                  options={{ currency: 'USD' }}
                  fontSize="4"
                  variant="dynamic"
                />
              </Flex>

              <Flex
                fontSize="-1"
                flexDirection="column"
                gap="3xs"
                alignItems="flex-end">
                <KpiTitle color="muted" fontSize="-1">
                  TVL
                </KpiTitle>
                <CurrencyValue
                  value={row.tvl}
                  options={{ currency: 'USD' }}
                  fontSize="4"
                  variant="dynamic"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex justifyContent="space-between" gap="3xs" alignItems="center">
            <Flex
              fontSize="-1"
              flexDirection="column"
              gap="3xs"
              alignItems="flex-start">
              <KpiTitle color="muted" fontSize="-1">
                N° of suppliers
              </KpiTitle>
              <CurrencyValue
                value={row.suppliers}
                fontSize="1"
                variant="dynamic"
                options={{ notation: 'standard' }}
              />
            </Flex>
            <Flex
              fontSize="-1"
              flexDirection="column"
              gap="3xs"
              alignItems="flex-end">
              <KpiTitle color="muted" fontSize="-1">
                N° of borrowers
              </KpiTitle>
              <CurrencyValue
                value={row.borrowers}
                fontSize="1"
                options={{ notation: 'standard' }}
              />
            </Flex>
          </Flex>
        </LinkCard>
      ))}
    </Flex>
  );
}
